import { useMemo, useRef } from "react";

/**
 * Stringifies a Record<string, string> with sorted keys
 * to ensure order does not affect comparison.
 */
function stableStringify(searchParams: Record<string, string>): string {
  const sortedEntries = Object.entries(searchParams).sort(([a], [b]) => a.localeCompare(b));
  return sortedEntries.map(([key, value]) => `${key}=${value}`).join("&");
}

/**
 * Memoizes a Record<string, string> object to keep it stable
 * unless the actual parameters (ignoring order) have changed.
 */
export function useStableSearchParams(
  searchParams: Record<string, string>,
): Record<string, string> {
  const prevStringified = useRef<string>("");
  const prevStableParams = useRef<Record<string, string>>(searchParams);

  const stringified = stableStringify(searchParams);

  return useMemo(() => {
    if (prevStringified.current === stringified) {
      // No change, return previous
      return prevStableParams.current;
    } else {
      // Params changed, update refs
      prevStringified.current = stringified;
      prevStableParams.current = searchParams;
      return searchParams;
    }
  }, [stringified]);
}
